import { useRef, useState } from "react";
import { ArrowDown, FileText, Paperclip, RotateCcw, SendHorizontal, X } from "lucide-react";
import { Button, EmptyState, Input, NoticeBox, Tags } from "../ui";
import { categoryLabel } from "./copilotText";
import { addFiles, eventText, formatFileSize, formatMessageTime } from "./format";
import { EMPTY_DRAFT } from "./useCopilotController";
import { useStickyScroll } from "./useStickyScroll";

const STATUS_VARIANT = { open: "success", pending: "warning", resolved: "neutral" };

function Attachment({ file }) {
  const isImage = file.mimeType?.startsWith("image/");
  return (
    <a className="lc-attachment" href={file.url} target="_blank" rel="noopener noreferrer">
      {isImage ? <img src={file.url} alt={file.name} loading="lazy" /> : <FileText size={18} aria-hidden="true" />}
      <span className="lc-attachment-name">{file.name}</span>
      <span className="lc-file-size">{formatFileSize(file.size)}</span>
    </a>
  );
}

function Bubble({ message, lang, t }) {
  if (message.kind === "event") {
    return (
      <li className="lc-event">
        <span>{eventText(message.text, lang)}</span>
        <time dateTime={message.createdAt}>{formatMessageTime(message.createdAt, lang)}</time>
      </li>
    );
  }
  const mine = message.senderType === "customer";
  const who = mine ? t.you : message.senderType === "ai" ? t.assistant : message.senderName || t.team;
  return (
    <li className={`lc-msg ${mine ? "mine" : "theirs"}`}>
      <span className="lc-msg-who">{who}</span>
      <div className="lc-bubble">
        {message.text && <p>{message.text}</p>}
        {message.attachments?.map((file) => <Attachment key={file.id} file={file} />)}
      </div>
      <span className="lc-msg-time">
        <time dateTime={message.createdAt}>{formatMessageTime(message.createdAt, lang)}</time>
        {!mine && !message.read && <span className="lc-new-flag" aria-hidden="true" />}
      </span>
    </li>
  );
}

function PendingBubble({ entry, c, id }) {
  const { t } = c;
  const failed = entry.status === "failed";
  return (
    <li className={`lc-msg mine pending ${failed ? "failed" : ""}`}>
      <span className="lc-msg-who">{t.you}</span>
      <div className="lc-bubble">
        {entry.text && <p>{entry.text}</p>}
        {entry.files.map((file, index) => (
          <span className="lc-attachment" key={`${file.name}-${index}`}>
            <FileText size={18} aria-hidden="true" />
            <span className="lc-attachment-name">{file.name}</span>
          </span>
        ))}
      </div>
      <span className="lc-msg-time" role={failed ? "alert" : undefined}>
        {failed ? (
          <>
            <span className="lc-failed">{entry.error || t.failed}</span>
            <Button variant="primary" appearance="text" size="xs" icon={<RotateCcw size={12} aria-hidden="true" />} onClick={() => c.retry(id, entry.key)}>{t.retrySend}</Button>
            <Button variant="neutral" appearance="text" size="xs" icon={<X size={12} aria-hidden="true" />} onClick={() => c.discardPending(id, entry.key)}>{t.discard}</Button>
          </>
        ) : (
          t.sending
        )}
      </span>
    </li>
  );
}

export default function ChatView({ c, lang }) {
  const { t, activeId: id, categories } = c;
  const chat = c.chats[id] || { messages: [], pending: [], status: "loading" };
  const conversation = chat.conversation;
  const draft = c.drafts[id] || EMPTY_DRAFT;
  const [fileError, setFileError] = useState(null);
  const fileInput = useRef(null);
  const textarea = useRef(null);

  const messages = chat.messages;
  const pending = chat.pending || [];
  const lastSeq = messages.length ? messages[messages.length - 1].seq : 0;
  const { scroller, onScroll, showNew, jumpToBottom, rememberPosition } = useStickyScroll({
    id,
    lastSeq,
    count: messages.length,
    pendingCount: pending.length,
    ownLast: messages[messages.length - 1]?.senderType === "customer",
    prepended: chat.prepended,
  });

  const loadEarlier = () => {
    rememberPosition();
    c.loadEarlier(id);
  };

  const onFiles = (event) => {
    const result = addFiles(draft.files, Array.from(event.target.files || []), t);
    c.setDraft(id, { files: result.files });
    setFileError(result.error);
    event.target.value = "";
  };

  const submit = (event) => {
    event?.preventDefault();
    c.send(id);
    textarea.current?.focus();
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  if (chat.status === "expired") {
    return (
      <EmptyState
        variant="error"
        className="lc-fill"
        title={t.expiredTitle}
        action={
          <>
            <Button variant="primary" appearance="filled" size="sm" onClick={() => c.startIntake()}>{t.newConversation}</Button>
            <Button variant="neutral" appearance="ghost" size="sm" onClick={c.showList}>{t.back}</Button>
          </>
        }
      >
        {t.expiredBody}
      </EmptyState>
    );
  }
  if (chat.status === "notfound") {
    return (
      <EmptyState variant="error" className="lc-fill" action={<Button variant="neutral" appearance="outline" size="sm" onClick={c.showList}>{t.back}</Button>}>{t.notFound}</EmptyState>
    );
  }
  if (chat.status === "error" && !messages.length) {
    return (
      <EmptyState variant="error" className="lc-fill" action={<Button variant="neutral" appearance="outline" size="sm" onClick={() => c.refreshChat(id)}>{t.retry}</Button>}>{chat.error || t.chatError}</EmptyState>
    );
  }

  const tooLong = draft.text.length > 5000;
  const canSend = (draft.text.trim() || draft.files.length) && !tooLong;

  return (
    <div className="lc-chat">
      {conversation && (
        <div className="lc-chat-head">
          <div className="lc-chat-title">
            <strong>{conversation.subject}</strong>
            <Tags size="sm" variant={STATUS_VARIANT[conversation.status] || "neutral"}>{t.status[conversation.status] || conversation.status}</Tags>
          </div>
          <div className="lc-chat-sub">
            <span>{categoryLabel(categories, conversation.category, lang)}</span>
            <span dir="ltr">{conversation.reference}</span>
          </div>
        </div>
      )}

      <div className="lc-scroll" ref={scroller} onScroll={onScroll}>
        {chat.status === "loading" && !messages.length && <EmptyState variant="loading" compact>{t.loading}</EmptyState>}
        {chat.hasMore && (
          <div className="lc-more">
            <Button variant="neutral" appearance="ghost" size="sm" loading={chat.loadingEarlier} onClick={loadEarlier}>{t.loadEarlier}</Button>
          </div>
        )}
        <ol className="lc-messages" role="log" aria-live="polite" aria-relevant="additions" aria-label={conversation?.subject || t.title}>
          {messages.map((message) => <Bubble key={message.id} message={message} lang={lang} t={t} />)}
          {pending.map((entry) => <PendingBubble key={entry.key} entry={entry} c={c} id={id} />)}
        </ol>
        {chat.error && messages.length > 0 && <NoticeBox variant="danger" size="sm">{chat.error}</NoticeBox>}
      </div>

      {showNew && (
        <Button variant="primary" appearance="filled" size="sm" rounded className="lc-new-pill" icon={<ArrowDown size={14} aria-hidden="true" />} onClick={jumpToBottom}>{t.newMessages}</Button>
      )}

      {conversation?.status === "resolved" && <NoticeBox variant="info" size="sm" className="lc-reopen">{t.reopenNotice}</NoticeBox>}

      <form className="lc-composer" onSubmit={submit}>
        {draft.files.length > 0 && (
          <ul className="lc-files">
            {draft.files.map((file, index) => (
              <li key={`${file.name}-${index}`}>
                <span className="lc-file-name">{file.name}</span>
                <span className="lc-file-size">{formatFileSize(file.size)}</span>
                <Button variant="neutral" appearance="ghost" size="xs" aria-label={`${t.removeFile}: ${file.name}`} icon={<X size={14} />} onClick={() => c.setDraft(id, { files: draft.files.filter((_, i) => i !== index) })} />
              </li>
            ))}
          </ul>
        )}
        {fileError && <NoticeBox variant="danger" size="sm">{fileError}</NoticeBox>}
        <div className="lc-composer-row">
          <input ref={fileInput} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onFiles} />
          <Button variant="neutral" appearance="ghost" className="lc-attach" aria-label={t.attach} title={t.attach} onClick={() => fileInput.current?.click()} icon={<Paperclip size={18} />} />
          <Input
            textarea
            ref={textarea}
            className="lc-composer-input"
            rows={1}
            aria-label={t.composerLabel}
            placeholder={t.composerPlaceholder}
            value={draft.text}
            onChange={(event) => c.setDraft(id, { text: event.target.value })}
            onKeyDown={onKeyDown}
            error={tooLong}
          />
          <Button type="submit" variant="primary" appearance="filled" rounded className="lc-send" aria-label={t.send} title={t.send} disabled={!canSend} icon={<SendHorizontal size={18} />} />
        </div>
        {tooLong && <NoticeBox variant="danger" size="sm">{t.messageLong}</NoticeBox>}
      </form>
    </div>
  );
}
